import { SectionStatus } from "components/office-visits/OfficeVisit/DotExam/PhysicalExam/interfaces";
import { FormStatus } from "constants/surveys";
import dayjs from "dayjs";
import Advanced from "dayjs/plugin/advancedFormat";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import Timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { everythingExceptDigits } from "utilities/regex";

// see https://day.js.org/docs/en/display/format#localized-formats
dayjs.extend(LocalizedFormat);
dayjs.extend(Advanced);
dayjs.extend(utc);
dayjs.extend(Timezone);

export { camelCase, kebabCase, snakeCase } from "lodash";

export function isEmpty(value: Nullable<string>): boolean {
  return (value || "").trim() === "";
}

export function isPresent(value: Nullable<string>) {
  return !isEmpty(value);
}

export function presence(value: Nullable<string>) {
  return isPresent(value) ? value : null;
}

export function squish(str: string) {
  return str.replace(/\s+/g, " ").trim();
}

export function convertToNullIfEmpty(inputValue: string | null): string | null {
  return inputValue === "" || inputValue === null ? null : inputValue;
}

export function camelToSnakeCase(str: string) {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export function formatSsn(str: string | null) {
  const text = str ?? "";
  if (text.length === 9) {
    return `${text.substr(0, 3)}-${text.substr(3, 2)}-${text.substr(5, 4)}`;
  }

  return text;
}

export function stringifyNumberWithCommas(num: Number): string {
  const stringWithCommas = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return stringWithCommas;
}

// #region string phone number functions

export function stripOutNonNumerics(phoneNumber: string | null): string {
  // remove any non-digit characters: "(616) 774 - 2000" => "2126260000"
  if (phoneNumber === "" || phoneNumber === null) return "";
  return phoneNumber.replace(everythingExceptDigits, "");
}

export function humanReadablePhone(phoneNumber: string | null): string {
  // format "2126260000" =>"(616) 774 - 2000"
  if (phoneNumber === "" || phoneNumber == null) return "";
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} - ${phoneNumber.slice(6)}`;
}

// #region string date functions
const formatWithTimezoneDefaults = {
  // => '04/13/2022 UTC'
  format: "L z",
  timezone: "UTC",
};
export function formatDateWithTimezone(value: string, opts = {}) {
  if (!value) {
    return "";
  }
  const conversionParams = { ...formatWithTimezoneDefaults, ...opts };
  return dayjs.tz(value, conversionParams.timezone).format(conversionParams.format);
}

export function convertUtcToLocal(utcValue: string, opts = {}) {
  const conversionParams = { ...formatWithTimezoneDefaults, ...opts };
  return dayjs(utcValue).format(conversionParams.format);
}

export function convertLocalToUtc(localValue: Date) {
  return dayjs(localValue).utc().toISOString();
}

export function convertUtcToTimezone(value: string | Date, opts = {}) {
  const conversionParams = { ...formatWithTimezoneDefaults, ...opts };
  if (!value) {
    return "-";
  }
  return dayjs.utc(value).tz(conversionParams.timezone).format(conversionParams.format);
}

export function navItemStatus(status: SectionStatus | null) {
  if (!status) {
    return FormStatus.NotStarted;
  }

  if (status.complete) {
    if (status.failure) {
      return FormStatus.Invalid;
    }

    return FormStatus.Complete;
  }

  if (status.concernCount! >= 0) {
    return FormStatus.Incomplete;
  }

  if (status.failure) {
    return FormStatus.Incomplete;
  }

  return FormStatus.NotStarted;
}

export function toTitleCase(str: string): string {
  if (!str) {
    return "";
  }

  return str.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  );
}

export function toTitle(str: string): string {
  if (!str) {
    return "";
  }

  return str
    .split(" ")
    .map((part) =>
      part.replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.substring(1))
    )
    .join(" ");
}

export function snakeCasetoTitleCase(str: string): string {
  return str
    .split("_")
    .map((part) =>
      part.replace(
        /\w\S*/g,
        (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
      )
    )
    .join(" ");
}
